// ─────────────────────────────────────────────
//  native/biometrics.js — biometric unlock helpers (Phase 6, D-08/D-09)
//
//  Thin wrapper over @aparajita/capacitor-biometric-auth, loaded lazily so
//  the plugin never enters the web bundle's critical path or the vitest
//  environment. All functions degrade gracefully: on web, on devices with
//  no enrolled biometrics, or on plugin failure they report "unavailable"
//  rather than throwing into the UI.
//
//  Anesthesia Team Control (ATC)
//  Copyright © 2026 Gonzalo Martinez (gamc00@gmail.com). All rights reserved.
// ─────────────────────────────────────────────
import { isNative } from "./platform";

/** Can this device do a biometric (or device-credential) unlock right now? */
export async function biometricsAvailable() {
  if (!isNative()) return false;
  try {
    const { BiometricAuth } = await import("@aparajita/capacitor-biometric-auth");
    const info = await BiometricAuth.checkBiometry();
    // strongBiometryIsAvailable covers Class-3 sensors; fall back to the
    // general flag so face/iris/credential-capable devices still qualify.
    return !!(info.isAvailable || info.strongBiometryIsAvailable);
  } catch {
    return false;
  }
}

/**
 * Prompt the native biometric sheet. Resolves true on success, false on
 * cancel/failure — never throws (D-09 graceful fallback).
 */
export async function biometricAuthenticate(reason = "Unlock Anesthesia Team Control") {
  if (!isNative()) return true; // web: nothing to unlock
  try {
    const { BiometricAuth } = await import("@aparajita/capacitor-biometric-auth");
    await BiometricAuth.authenticate({
      reason,
      androidTitle: "Unlock ATC",
      androidSubtitle: reason,
      allowDeviceCredential: true, // PIN/pattern fallback if biometry fails
      cancelTitle: "Cancel",
    });
    return true;
  } catch {
    return false;
  }
}
